import type { ColDef, ValueFormatterParams } from 'ag-grid-community'
import type { PlayerFamilyRow } from '@sportninja/common/features/profile/player/playerDetails'
import { formatPhoneNumber } from 'react-phone-number-input'
import FamilyStatusCell from '../components/FamilyStatusCell'

/** E.164 → national format, as the family list shows it; raw if unparseable. */
const formatPhone = <TRow>(params: ValueFormatterParams<TRow, string>) =>
  params.value ? formatPhoneNumber(params.value) || params.value : ''

/**
 * Family table columns for any row that extends PlayerFamilyRow (the read-only
 * details grid, and the editable section whose rows also carry the member).
 * Columns read their value through `valueGetter` + `colId`: AG Grid's `field`
 * key can't be checked against a generic row type. Call once at module level
 * so the column identity stays stable.
 */
export const familyColumns = <
  TRow extends PlayerFamilyRow
>(): ColDef<TRow>[] => [
  {
    colId: 'name',
    valueGetter: ({ data }) => data?.name,
    headerName: 'Name',
    flex: 1.2,
    minWidth: 160,
  },
  {
    colId: 'relationship',
    valueGetter: ({ data }) => data?.relationship,
    headerName: 'Relationship',
    flex: 0.8,
    minWidth: 120,
  },
  {
    colId: 'email',
    valueGetter: ({ data }) => data?.email,
    headerName: 'Email',
    flex: 1.4,
    minWidth: 200,
  },
  {
    colId: 'phone',
    valueGetter: ({ data }) => data?.phone,
    headerName: 'Phone Number',
    flex: 1,
    minWidth: 150,
    valueFormatter: formatPhone,
  },
  {
    colId: 'status',
    valueGetter: ({ data }) => data?.status,
    headerName: 'Status',
    flex: 1,
    minWidth: 170,
    cellRenderer: FamilyStatusCell,
  },
]
